import { lineItemsGross, parseSeats } from "./seat-defaults"
import type { DiscountMode, DiscountType, LineItemForm } from "./types"

/**
 * Pure client-side mirror of the backend discount cascade
 * (``invoicer.invoices.service.build_discount_lines`` /
 * ``compute_net_amount``) used to show the operator the resulting net
 * while they edit discounts in the amend modal. The backend recomputes
 * authoritatively on submit; this is advisory.
 *
 * The cascade is sequential and clamped: the standard discount applies
 * first to the (whole-unit) list price, then each named discount applies
 * to the running remainder, each step clamped so the net never drops
 * below zero. Billing precision is whole major units for every currency,
 * so amounts are rounded half-up to whole units — matching ``round_money``.
 */

export interface DiscountPreviewLine {
  /** Display label — "Discount" for the standard line, else the name. */
  label: string
  /** The absolute reduction at this step, in whole major units. */
  amount: number
}

export interface DiscountPreview {
  /** List price rounded to a whole unit. */
  gross: number
  /** One line per applied discount, in cascade order. */
  lines: DiscountPreviewLine[]
  /** The net the customer owes, in whole major units. */
  net: number
}

/** A named discount's pricing inputs, resolved from the catalog. */
export interface NamedDiscountInput {
  name: string
  type: DiscountType
  value: string
}

function roundWhole(amount: number): number {
  // Billing precision: whole major units, half-up. Amounts here are
  // non-negative, where Math.round already rounds .5 up — matching the
  // backend's ROUND_HALF_UP.
  return Math.round(amount)
}

function nextRemainder(
  type: DiscountType,
  value: number,
  running: number,
): number {
  const next =
    type === "percent" ? (running * (100 - value)) / 100 : running - value
  // Round the REMAINDER, not just the step — mirroring the backend's
  // `_next_remainder`, whose docstring explains why: rounding the
  // remainder is what makes each applied step (`running - next`)
  // telescope, so the lines sum exactly to `gross - net`. Rounding only
  // the step and the final net rounds the same cascade twice from two
  // directions: 1195 at 10% renders "Discount −120" against a net of
  // 1076, which do not add up to 1195, and a two-discount cascade drifts
  // a whole unit away from what the server will charge (1005 at 10% then
  // 10% is 815 there, 814 here).
  return roundWhole(next < 0 ? 0 : next)
}

export function buildDiscountPreview(args: {
  amount: number
  standardMode: DiscountMode
  standardValue: string
  namedDiscounts: NamedDiscountInput[]
}): DiscountPreview {
  const gross = roundWhole(args.amount)
  let running = gross
  const lines: DiscountPreviewLine[] = []

  if (args.standardMode !== "none") {
    const value = Number(args.standardValue)
    if (Number.isFinite(value) && value > 0) {
      const next = nextRemainder(args.standardMode, value, running)
      lines.push({ label: "Discount", amount: roundWhole(running - next) })
      running = next
    }
  }

  for (const named of args.namedDiscounts) {
    const value = Number(named.value)
    if (!Number.isFinite(value)) continue
    const next = nextRemainder(named.type, value, running)
    lines.push({ label: named.name, amount: roundWhole(running - next) })
    running = next
  }

  return { gross, lines, net: roundWhole(running) }
}

/**
 * Resolve the rep's attached named-discount ids into cascade inputs, or
 * `null` when the invoice **cannot be priced**: the catalog has not
 * answered (`undefined` — still loading, or the request failed), or an
 * attached id has no row in it (a discount deactivated since the draft was
 * saved).
 *
 * Order follows `selectedIds`, because the cascade is sequential — each
 * named discount applies to the remainder the previous one left — so
 * re-ordering the ids genuinely changes the net.
 *
 * `null` rather than "price the ones we recognise": every id here reduces
 * the net, so dropping one quotes the customer a price **higher** than
 * they will be charged — the exact failure this whole chokepoint exists to
 * stop, and one the rep has no way to notice. Declining to answer costs a
 * preview; guessing costs a wrong number on a sales call. (The server
 * refuses the submit either way: an unknown or inactive id is a 422.)
 *
 * Lives beside the cascade rather than beside either caller because both
 * the deposit preview and the payment-method picker's charge amount feed
 * it into `buildDiscountPreview` — and when they each owned their own
 * version of this rule they disagreed, one declining to answer while the
 * other quoted the too-high net.
 */
export function resolveNamedDiscounts(
  catalog:
    | readonly {
        id: string
        name: string
        discount_type: NamedDiscountInput["type"]
        discount_value: string
      }[]
    | undefined,
  selectedIds: readonly string[],
): NamedDiscountInput[] | null {
  if (selectedIds.length === 0) return []
  if (catalog === undefined) return null
  const byId = new Map(catalog.map((row) => [row.id, row]))
  const resolved: NamedDiscountInput[] = []
  for (const id of selectedIds) {
    const row = byId.get(id)
    if (row === undefined) return null
    resolved.push({
      name: row.name,
      type: row.discount_type,
      value: row.discount_value,
    })
  }
  return resolved
}

/**
 * The invoice's **net** order total as the form currently stands — the
 * figure the customer actually owes — in whole major units of the issue
 * currency, or `null` when it is not yet knowable.
 *
 * The single client-side answer to "how much is this invoice worth?", and
 * the reason it exists: the gross roll-up (`lineItemsGross`) and the
 * discount cascade (`buildDiscountPreview`) were two separate steps that
 * only the payment-method picker ever ran together, so every other
 * consumer sized the invoice off the pre-discount gross. That is how the
 * deposit split preview came to advertise a 50/50 of the list price on a
 * discounted invoice (£596 / £596 against a real £537 / £536) while the
 * backend — which splits `compute_net_amount` — charged the discounted
 * halves.
 *
 * Mirrors the server exactly: round each per-seat unit, multiply by
 * seats, sum the courses (`lineItemsGross`), then apply the standard
 * discount followed by each named discount on the running remainder,
 * clamped at zero and rounded to whole major units (`buildDiscountPreview`
 * / `compute_net_amount`).
 *
 * `null` means "cannot say": the rows have not settled (no price typed, a
 * currency conversion in flight) or the attached named discounts cannot be
 * priced (`resolveNamedDiscounts`). It never means "zero" — a fully
 * discounted invoice returns `0`, which callers must distinguish: it is
 * too small to split, not unknown.
 */
export function orderNetTotal(args: {
  lineItems: readonly LineItemForm[]
  seats: string
  standardMode: DiscountMode
  standardValue: string
  /** `null` = the attached discounts cannot be priced (`resolveNamedDiscounts`). */
  namedDiscounts: readonly NamedDiscountInput[] | null
}): number | null {
  if (args.namedDiscounts === null) return null
  const gross = lineItemsGross(args.lineItems, parseSeats(args.seats))
  if (gross === null) return null
  const { net } = buildDiscountPreview({
    amount: gross,
    standardMode: args.standardMode,
    standardValue: args.standardValue,
    namedDiscounts: [...args.namedDiscounts],
  })
  return net
}
